import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import useFriendsOfDwf from "../../lib/hooks/use-friends-of-dwf";
import { partnerFieldIds } from "../../lib/airtables/tables/Partners";

type FriendsOfDwfPartnerPageProps = {
  partnerSlug: string;
  fallbackTitle?: string;
  fallbackDescription?: string;
  fallbackInfoTitle?: string;
};

export default function FriendsOfDwfPartnerPage({
  partnerSlug,
  fallbackTitle,
  fallbackDescription,
  fallbackInfoTitle,
}: FriendsOfDwfPartnerPageProps) {
  const [loading, setLoading] = useState(true);
  const { isLoading, getPartnerBySlug, buildPartnerLinks } = useFriendsOfDwf();
  const partner = getPartnerBySlug(partnerSlug);

  const partnerDetails = useMemo(() => {
    const normalizedFallbackTitle =
      fallbackTitle ||
      partnerSlug
        .split("-")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
    const normalizedFallbackDescription =
      fallbackDescription || `Resources from our partner ${normalizedFallbackTitle}.`;
    const normalizedFallbackInfoTitle =
      fallbackInfoTitle || `About ${normalizedFallbackTitle}`;

    const partnerName = partner?.[partnerFieldIds.name]?.trim();
    const title = partnerName || normalizedFallbackTitle;
    const about = partner?.[partnerFieldIds.about] || normalizedFallbackDescription;
    const partnerInfoTitle = partnerName
      ? `About ${partnerName}`
      : normalizedFallbackInfoTitle;
    const partnerInfoDescription = about;
    const headerDescription = partnerName
      ? `Resources from our partner ${partnerName}.`
      : normalizedFallbackDescription;
    const airtableUrl = partner?.[partnerFieldIds.interfacePage];
    const partnerLinks = partner ? buildPartnerLinks(partner) : [];

    return {
      title,
      about,
      headerDescription,
      partnerInfoTitle,
      partnerInfoDescription,
      airtableUrl,
      partnerLinks,
    };
  }, [
    buildPartnerLinks,
    fallbackDescription,
    fallbackInfoTitle,
    fallbackTitle,
    partnerSlug,
    partner,
  ]);

  return (
    <main className="friends-of-dwf-page page bg-white">
      <div className="max-w-5xl mx-auto px-4 py-10">
        <Link
          to="/app/friends-of-dwf"
          className="inline-flex items-center text-sm font-semibold token-link-primary mb-6"
        >
          ← Back to Friends of DWF
        </Link>
        <section className="token-surface-partner border token-border-partner rounded-3xl p-6 sm:p-8">
          <h1 className="text-2xl font-semibold text-gray-800 mb-3">
            {partnerDetails.title}
          </h1>
          <p className="text-gray-600 mb-8">
            {isLoading && !partnerDetails.headerDescription
              ? "Loading partner details..."
              : partnerDetails.headerDescription}
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6">
            <div>
              {partnerDetails.airtableUrl ? (
                <div className="w-full h-[60vh] relative rounded-2xl overflow-hidden border token-border-partner-muted bg-white/70">
                  {loading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
                      <span className="loader"></span>
                    </div>
                  )}
                  <iframe
                    title={`${partnerDetails.title} Resources`}
                    className="airtable-embed"
                    src={partnerDetails.airtableUrl}
                    frameBorder="0"
                    width="100%"
                    height="100%"
                    style={{ background: "transparent", border: "1px solid var(--color-border-subtle)" }}
                    onLoad={() => setLoading(false)}
                  />
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed token-border-partner-muted bg-white/70 p-10 text-center text-gray-500">
                  Airtable partner resources will appear here.
                </div>
              )}
            </div>
            <aside className="rounded-2xl border token-border-partner bg-white/70 p-6 text-gray-700">
              <h2 className="text-lg font-semibold text-gray-800 mb-3">
                {partnerDetails.partnerInfoTitle}
              </h2>
              <p className="text-sm text-gray-600 mb-6">
                {partnerDetails.partnerInfoDescription}
              </p>
              {partnerDetails.partnerLinks.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">
                    Links
                  </p>
                  <ul className="space-y-2 text-sm">
                    {partnerDetails.partnerLinks.map((link) => (
                      <li key={link.url}>
                        <a
                          href={link.url}
                          className="token-link-primary underline"
                          target="_blank"
                          rel="noreferrer"
                        >
                          {link.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </aside>
          </div>
        </section>
      </div>
    </main>
  );
}
